import React from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { panel } from './panelTheme';
import { PropertiesPanelNumberField } from './propertiesPanelNumberField';
import { PropertiesPanelPickerField } from './propertiesPanelPicker';
import { PropertiesPanelSwitch } from './propertiesPanelSwitchProperty';

// IL MAPPING DI UNA TEXTURE — l'interruttore che lo scavalca e, sotto, canale UV, tiling, offset
// e rotazione.
//
// UN BLOCCO SOLO E NON QUATTRO RIGHE DEL PANNELLO. Le righe del mapping hanno tutte le stesse
// colonne: il menu del canale UV e il campo della rotazione sono larghi quanto uno dei due assi e
// cominciano dove comincia U (mockup, DS `.map-form`). Quattro righe qualunque si misurerebbero
// ognuna da sé, e finirebbero a quattro x diverse.
//
// SPENTO, I CAMPI NON CI SONO — non sono grigi. Un campo grigio dice «questo valore c'è, ma non si
// tocca»; spento qui vuol dire che la texture non ha un mapping suo, e i numeri che si
// mostrerebbero non varrebbero niente.
//
// L'INTERRUTTORE STA ALLA X DELLO SLOT e i nomi sotto rientrano: non è un parametro fra i
// parametri, è ciò che decide se i parametri esistono.

type MappingValue = { value: number; onChange: (value: number) => void };

export type PropertiesPanelTextureMappingPropertyProps = {
    id: string;
    /** Il nome dell'interruttore. */
    label: React.ReactNode;
    overridden: boolean;
    onOverride: (overridden: boolean) => void;
    /** I canali UV fra cui scegliere, per indice: 0 si legge «UV0». */
    uvSets: number[];
    uvSet: MappingValue;
    tiling: [MappingValue, MappingValue];
    offset: [MappingValue, MappingValue];
    /** In gradi: in che unità la texture la tenga lo sa chi ci scrive. */
    rotation: MappingValue;
    disabled?: boolean;
};

const Block = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

// I 4PX SONO IL RIENTRO DELLO SLOT: il suo contenuto comincia più in là del suo bordo, e un nome
// sulla x esatta del bordo a occhio finirebbe prima.
const OverrideRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 26px;
    padding-left: 4px;
`;

const OverrideLabel = styled.span`
    font-size: 12px;
    color: ${panel.ink};
`;

// RIENTRO, NOME, VALORI — 18 e 56 come nel mockup. Il rientro dice che queste righe dipendono
// dall'interruttore sopra.
const Row = styled.div`
    display: grid;
    grid-template-columns: 18px 56px minmax(0, 1fr);
    column-gap: 8px;
    align-items: center;
`;

const Name = styled.label`
    grid-column: 2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: ${panel.inkMuted};
`;

// Il passo fra i due assi è 4, come fra i tre di Transform.
const Pair = styled.div`
    grid-column: 3;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    min-width: 0;
`;

// Il valore da solo è largo quanto un asse, e comincia dove comincia U.
const Single = styled.div`
    grid-column: 3;
    display: flex;
    width: calc(50% - 2px);
    min-width: 0;

    & > * {
        flex: 1 1 auto;
        min-width: 0;
    }
`;

// U E V PORTANO I COLORI DI X E Y: sono gli stessi due assi, in un altro spazio.
const AxisPair: React.FC<{ id: string; name: string; values: [MappingValue, MappingValue]; step: number; disabled?: boolean }> = ({
    id,
    name,
    values,
    step,
    disabled
}) => (
    <Row>
        <Name htmlFor={`${id}-u`}>{name}</Name>
        <Pair>
            {(['u', 'v'] as const).map((coordinate, index) => (
                <PropertiesPanelNumberField
                    key={coordinate}
                    id={`${id}-${coordinate}`}
                    value={values[index].value}
                    step={step}
                    axis={coordinate === 'u' ? 'x' : 'y'}
                    letter={coordinate.toUpperCase()}
                    full
                    ariaLabel={`${name} ${coordinate.toUpperCase()}`}
                    disabled={disabled}
                    onChange={values[index].onChange}
                />
            ))}
        </Pair>
    </Row>
);

export const PropertiesPanelTextureMappingProperty: React.FC<PropertiesPanelTextureMappingPropertyProps> = ({
    id,
    label,
    overridden,
    onOverride,
    uvSets,
    uvSet,
    tiling,
    offset,
    rotation,
    disabled
}) => {
    const options = uvSets.map((index) => ({ value: index, label: 'UV' + index }));

    return (
        <Block>
            <OverrideRow>
                <OverrideLabel id={`${id}-label`}>{label}</OverrideLabel>
                <PropertiesPanelSwitch id={id} checked={overridden} labelledBy={`${id}-label`} disabled={disabled} onChange={onOverride} />
            </OverrideRow>

            {overridden && (
                <>
                    <Row>
                        <Name as='span'>{T._('UV set', 'Admin')}</Name>
                        <Single>
                            <PropertiesPanelPickerField
                                id={`${id}-uv-set`}
                                options={options}
                                value={uvSet.value}
                                onChange={(next) => uvSet.onChange(Number(next))}
                                disabled={disabled}
                                ariaLabel={T._('UV set', 'Admin')}
                            />
                        </Single>
                    </Row>
                    <AxisPair id={`${id}-tiling`} name={T._('Tiling', 'Admin')} values={tiling} step={0.01} disabled={disabled} />
                    <AxisPair id={`${id}-offset`} name={T._('Offset', 'Admin')} values={offset} step={0.01} disabled={disabled} />
                    <Row>
                        <Name htmlFor={`${id}-rotation`}>{T._('Rotation', 'Admin')}</Name>
                        <Single>
                            <PropertiesPanelNumberField
                                id={`${id}-rotation`}
                                value={rotation.value}
                                step={1}
                                unit='°'
                                full
                                ariaLabel={T._('Rotation', 'Admin')}
                                disabled={disabled}
                                onChange={rotation.onChange}
                            />
                        </Single>
                    </Row>
                </>
            )}
        </Block>
    );
};
